import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { navigate } from 'gatsby';
import { gql, useMutation, useQuery } from '@apollo/client';

import { useTranslation } from 'react-i18next';
import useLocales from '../hooks/data/useLocales';
import useWithGA from '../hooks/useWithGA';
import useStrapiPageData from '../hooks/data/useStrapiPageData';

import SystemGuideModal from '../ui/organisms/systemGuide/systemGuideModal/SystemGuideModal';

import { PAGE_SYSTEMGUIDE_RESULT } from '../constants/strapi';

const GET_GUIDE_CATEGORIES_LIST = gql`
  query GetGuideCategoriesList($locale: String!) {
    getGuideCategoriesList(locale: $locale)
  }
`;

const START_GUIDE_MUTATION_GQL = gql`
  mutation StartGuideInstance($data: StartGuideInstanceInput!) {
    startGuideInstance(data: $data) {
      guideInstanceId
      category
      guideQuestions {
        id
        type
        order
        section
        texts
        textsShort
        textsHelp
        linksInfo
        name
        required
        answerAlternatives
        answersHeadings
        guideAnswers {
          id
          texts
          textsHelp
          section
          nextQuestion
          type
          order
          impact
          percentage
          specialName
        }
      }
    }
  }
`;

const COMPLETE_GUIDE_INSTANCE_GQL = gql`
  mutation CompleteGuideInstance(
    $guideInstanceId: ID!
    $answers: [GuideAnswersInput!]!
  ) {
    completeGuideInstance(
      guideInstanceId: $guideInstanceId
      answers: $answers
    ) {
      result
    }
  }
`;

const ABORT_GUIDE_INSTANCE_GQL = gql`
  mutation AbortGuideInstance(
    $guideInstanceId: ID!
    $answers: [GuideAnswersInput]
  ) {
    abortGuideInstance(guideInstanceId: $guideInstanceId, answers: $answers) {
      result
    }
  }
`;

const SAVE_PROGRESS_GUIDE_INSTANCE_GQL = gql`
  mutation SaveProgressGuideInstance(
    $guideInstanceId: ID!
    $questionId: ID!
    $answers: [GuideAnswersInput]!
    $nextQuestionId: ID
  ) {
    saveProgressGuideInstance(
      guideInstanceId: $guideInstanceId
      questionId: $questionId
      answers: $answers
      nextQuestionId: $nextQuestionId
    )
  }
`;

// Temporary solution??
const convertCompanySize = (companySize) => {
  switch (companySize) {
    case '1-10':
      return 'MICRO';
    case '11-100':
      return 'SMALL';
    case '101-500':
      return 'MEDIUM';
    case '500+':
      return 'LARGE';
  }
};

const BusinesswithSystemguide = forwardRef((props, ref) => {
  const { savePageView, handleDataLayer } = useWithGA();
  const { locale } = useLocales();
  const { t } = useTranslation();

  const [systemguideResultPageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE_RESULT);

  const [modalOpen, setModalOpen] = useState(false);
  const [guideInstanceId, setGuideInstanceId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [companySize, setCompanySize] = useState('');
  const [guideQuestions, setGuideQuestions] = useState([]);

  const [startGuideMutation, { loading: startGuideLoading }] = useMutation(
    START_GUIDE_MUTATION_GQL,
    {
      onCompleted: ({ startGuideInstance }) => {
        localStorage.setItem('guideId', startGuideInstance.guideInstanceId);
        setGuideInstanceId(startGuideInstance.guideInstanceId);
        setSelectedCategory(startGuideInstance.category);

        // Temporary make WHERE_PROCESS required
        setGuideQuestions(
          startGuideInstance.guideQuestions.map((question) => {
            const sortedGuideAnswers = question.guideAnswers.sort(
              (a, b) => a.order - b.order
            );

            if (question.name === 'WHERE_PROCESS') {
              return {
                ...question,
                guideAnswers: sortedGuideAnswers,
                required: true,
              };
            }

            return {
              ...question,
              guideAnswers: sortedGuideAnswers,
            };
          })
        );
      },
    }
  );

  const [saveProgressGuideInstanceMutation] = useMutation(
    SAVE_PROGRESS_GUIDE_INSTANCE_GQL
  );

  const [
    completeGuideMutation,
    { data: completeGuideData, loading: completeGuideLoading },
  ] = useMutation(COMPLETE_GUIDE_INSTANCE_GQL, {
    onCompleted: () => {
      navigate(systemguideResultPageUrl);
    },
  });

  const [abortGuideMutation] = useMutation(ABORT_GUIDE_INSTANCE_GQL);

  const { data: guideCategories } = useQuery(GET_GUIDE_CATEGORIES_LIST, {
    variables: {
      locale,
    },
  });

  const getGuideAnswers = useCallback(
    (answersObject) =>
      Object.values(answersObject)
        .filter((data) => !!data)
        .flat(),
    []
  );

  const handleStartGuide = useCallback(
    (choosenCategory, choosenCompanySize) => {
      const { href } = window.location;
      const { referrer } = window.frames.top.document;

      startGuideMutation({
        variables: {
          data: {
            adCode: sessionStorage.getItem('adCode') || null,
            adGclid: sessionStorage.getItem('adGclid') || null,
            adOriginalReferrer:
              sessionStorage.getItem('adOriginalReferrer') || null,
            browser: navigator.userAgent,
            category: selectedCategory || choosenCategory,
            companySize: convertCompanySize(companySize || choosenCompanySize),
            locale,
            referrer,
            sessionId: sessionStorage.getItem('sessionId') || null,
            url: href,
          },
        },
      });
    },
    [startGuideMutation, companySize, locale, selectedCategory]
  );

  const handleCompleteGuide = useCallback(
    (answersObject) => {
      const requestAnswersData = getGuideAnswers(answersObject);

      completeGuideMutation({
        variables: {
          guideInstanceId,
          answers: requestAnswersData,
        },
      });

      handleDataLayer({
        event_name: 'system_guide',
        event_action: 'Guide done',
        system_category: selectedCategory,
      });
    },
    [completeGuideMutation, getGuideAnswers, guideInstanceId]
  );

  // This function will/should not run when pressing the "Finish" button
  const handleSaveProgress = useCallback(
    (questionId, answersObject, nextQuestionId) => {
      const answers = answersObject ? getGuideAnswers(answersObject) : [];

      saveProgressGuideInstanceMutation({
        variables: {
          guideInstanceId,
          questionId,
          answers,
          nextQuestionId,
        },
      });
    },
    [saveProgressGuideInstanceMutation, getGuideAnswers, guideInstanceId]
  );

  const handleAbortGuide = useCallback(
    (answersObject) => {
      const requestAnswersData = getGuideAnswers(answersObject);

      if (guideInstanceId) {
        abortGuideMutation({
          variables: {
            guideInstanceId,
            answers: requestAnswersData,
          },
        });
      }

      setModalOpen(false);
      setGuideInstanceId(null);
      setSelectedCategory(null);
      setCompanySize('');
      setGuideQuestions([]);
    },
    [abortGuideMutation, getGuideAnswers, guideInstanceId]
  );

  useImperativeHandle(
    ref,
    () => ({
      startSystemGuide: ({ companySize: companySizeProps, category }) => {
        if (companySizeProps) {
          setCompanySize(companySizeProps);
        }

        if (category) {
          handleStartGuide(category, companySizeProps);
        }

        setModalOpen(true);

        handleDataLayer({
          event_name: 'system_guide',
          event_action: 'Guide start',
          company_size: companySizeProps,
          system_category: category || '',
          // company_role: updatedRole,
        });
      },
    }),
    [handleStartGuide]
  );

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [modalOpen]);

  useEffect(() => {
    // Do not run unless a category is selected
    if (selectedCategory) {
      savePageView({
        type: 'SYSTEMGUIDE',
        ids: [selectedCategory],
      });
    }
  }, [selectedCategory]);

  const handleSelectCategory = (choosenCategory) => {
    handleStartGuide(choosenCategory);
  };

  if (!modalOpen) {
    return null;
  }

  return (
    <SystemGuideModal
      isOpen={modalOpen}
      categories={guideCategories?.getGuideCategoriesList}
      guideQuestions={guideQuestions}
      onSelectCategory={handleSelectCategory}
      selectedCategory={selectedCategory}
      onComplete={handleCompleteGuide}
      onAbort={handleAbortGuide}
      onSaveProgress={handleSaveProgress}
      locale={locale}
      startGuideLoading={startGuideLoading}
      completeGuideLoading={
        completeGuideLoading ||
        completeGuideData?.completeGuideInstance?.result === 'done'
      }
      t={t}
    />
  );
});

BusinesswithSystemguide.displayName = 'BusinesswithSystemguide';

export default BusinesswithSystemguide;
